import React, { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { HexColorPicker } from 'react-colorful';
import { TextField } from 'modules/Common/form/v2';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { normalizeHexColor } from 'modules/Common/utils/brandColor';

// A handful of on-brand presets alongside the free-form picker. First is the
// Carbon Glance green default; the rest are common corporate accents.
const SWATCHES = ['#00b894', '#2563eb', '#7c3aed', '#0f172a', '#dc2626', '#0f766e', '#ea580c'];

// V2-field shim: TextField only needs {name, value, onChange, onBlur}. The hex
// value isn't part of a react-hook-form tree here, so reuse the real V2 input
// (D48: never a default HTML <input>) without a Form/Controller.
const manualField = (name, value, onChange) => ({ name, value, onChange, onBlur: () => {} });

// A persisted brand-color picker: a Radix popover fronting a react-colorful
// visual picker, preset swatches, and a hex field. `value` is the currently
// persisted color; `onApply(hex)` persists a new one (the caller owns the
// write). Editing is local (draft) until Apply, so dragging the picker never
// spams the backend.
export default function WidgetThemePicker({ value, onApply, saving = false }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  // Keep the draft synced to the persisted value on every open/close AND
  // whenever `value` itself changes. The value-change case is what prevents a
  // save from being silently reverted: after Apply, the popover closes while the
  // save is still in flight (so `value` is briefly stale); a close-only reseed
  // would leave the draft at the pre-save color, and a later Apply would write
  // that stale color back. Re-syncing when `value` lands fixes that.
  useEffect(() => {
    setDraft(value);
  }, [value, open]);

  const normalizedDraft = normalizeHexColor(draft);
  const apply = () => {
    if (!normalizedDraft) return;
    onApply(normalizedDraft);
    setOpen(false);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={setOpen}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
        >
          <span
            className="w-5 h-5 rounded-full border border-slate-200 shrink-0"
            style={{ background: value }}
          />
          <span className="font-mono text-xs text-slate-600">{value}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={8}
          align="start"
          className="z-50 w-[236px] rounded-2xl border border-slate-200 bg-white p-3 shadow-lg flex flex-col gap-3"
        >
          <HexColorPicker
            color={normalizedDraft || '#00b894'}
            onChange={setDraft}
            style={{ width: '100%' }}
          />
          <div className="flex flex-wrap gap-1.5">
            {SWATCHES.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setDraft(color)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${
                  normalizeHexColor(draft) === color
                    ? 'border-slate-700 scale-110'
                    : 'border-transparent'
                }`}
                style={{ background: color }}
                title={color}
              />
            ))}
          </div>
          <TextField
            field={manualField('primaryColor', draft, setDraft)}
            className="font-mono text-xs"
            placeholder="#00b894"
          />
          <CgRadixButton
            size="sm"
            variant="primary"
            className="w-full justify-center"
            disabled={!normalizedDraft || saving}
            onClick={apply}
          >
            {saving ? 'Saving…' : 'Apply color'}
          </CgRadixButton>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
